// ================================================================
// 管理員公告管理頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-27
// 功能：管理系統公告（新增、編輯、刪除、啟用/停用）
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Refresh,
  Add,
  Edit,
  Delete,
  Info,
  Warning,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  getAdminAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  toggleAnnouncementStatus,
  Announcement,
  AnnouncementStats,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
} from '../../api/admin';

// 類型圖示映射
const typeIcons: { [key: string]: React.ReactNode } = {
  info: <Info />,
  warning: <Warning />,
  success: <CheckCircle />,
  error: <Error />,
};

// 類型顏色映射
const typeColors: { [key: string]: 'info' | 'warning' | 'success' | 'error' } = {
  info: 'info',
  warning: 'warning',
  success: 'success',
  error: 'error',
};

// 類型名稱映射
const typeNames: { [key: string]: string } = {
  info: '一般資訊',
  warning: '警告通知',
  success: '成功訊息',
  error: '錯誤警示',
};

const AdminAnnouncementsPage: React.FC = () => {
  // 狀態
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [stats, setStats] = useState<AnnouncementStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // 編輯/新增對話框
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [formData, setFormData] = useState<CreateAnnouncementRequest>({
    title: '',
    content: '',
    type: 'info',
    priority: 0,
    is_active: 1,
    start_date: '',
    end_date: '',
  });

  // 刪除確認對話框
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState<Announcement | null>(null);

  // 載入公告列表
  const loadAnnouncements = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAdminAnnouncements();
      if (response.success) {
        setAnnouncements(response.announcements || []);
        setStats(response.stats || null);
      } else {
        setError(response.message || '無法載入公告列表');
      }
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  // 開啟新增對話框
  const handleOpenCreate = () => {
    setEditingAnnouncement(null);
    setFormData({
      title: '',
      content: '',
      type: 'info',
      priority: 0,
      is_active: 1,
      start_date: '',
      end_date: '',
    });
    setDialogOpen(true);
  };

  // 開啟編輯對話框
  const handleOpenEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      priority: announcement.priority,
      is_active: announcement.is_active,
      start_date: announcement.start_date || '',
      end_date: announcement.end_date || '',
    });
    setDialogOpen(true);
  };

  // 關閉對話框
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAnnouncement(null);
  };

  // 儲存公告
  const handleSave = async () => {
    if (!formData.title.trim()) {
      setError('請輸入公告標題');
      return;
    }
    if (!formData.content.trim()) {
      setError('請輸入公告內容');
      return;
    }

    // 驗證日期：開始日期不能晚於結束日期
    if (formData.start_date && formData.end_date && formData.start_date > formData.end_date) {
      setError('開始日期不能晚於結束日期');
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      if (editingAnnouncement) {
        // 更新
        const updateData: UpdateAnnouncementRequest = {
          title: formData.title,
          content: formData.content,
          type: formData.type,
          priority: formData.priority,
          is_active: formData.is_active,
          start_date: formData.start_date || undefined,
          end_date: formData.end_date || undefined,
        };
        const response = await updateAnnouncement(editingAnnouncement.announcement_id, updateData);
        if (response.success) {
          setSuccess('公告已更新');
          handleCloseDialog();
          loadAnnouncements();
        } else {
          setError(response.message || '更新失敗');
        }
      } else {
        // 新增
        const response = await createAnnouncement(formData);
        if (response.success) {
          setSuccess('公告已新增');
          handleCloseDialog();
          loadAnnouncements();
        } else {
          setError(response.message || '新增失敗');
        }
      }
    } catch (err: any) {
      setError(err.message || '操作失敗');
    } finally {
      setIsProcessing(false);
    }
  };

  // 切換狀態
  const handleToggle = async (announcement: Announcement) => {
    setIsProcessing(true);
    setError(null);
    try {
      const response = await toggleAnnouncementStatus(announcement.announcement_id);
      if (response.success) {
        setSuccess(response.message);
        loadAnnouncements();
      } else {
        setError(response.message || '操作失敗');
      }
    } catch (err: any) {
      setError(err.message || '操作失敗');
    } finally {
      setIsProcessing(false);
    }
  };

  // 開啟刪除確認
  const handleOpenDelete = (announcement: Announcement) => {
    setDeletingAnnouncement(announcement);
    setDeleteDialogOpen(true);
  };

  // 確認刪除
  const handleConfirmDelete = async () => {
    if (!deletingAnnouncement) return;

    setIsProcessing(true);
    setError(null);
    try {
      const response = await deleteAnnouncement(deletingAnnouncement.announcement_id);
      if (response.success) {
        setSuccess('公告已刪除');
        setDeleteDialogOpen(false);
        setDeletingAnnouncement(null);
        loadAnnouncements();
      } else {
        setError(response.message || '刪除失敗');
      }
    } catch (err: any) {
      setError(err.message || '刪除失敗');
    } finally {
      setIsProcessing(false);
    }
  };

  // 格式化日期
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading && announcements.length === 0) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        {/* 頁面標題 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              系統公告管理
            </Typography>
            <Typography variant="body2" color="text.secondary">
              管理系統公告，設定顯示時間與優先順序
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenCreate}
            >
              新增公告
            </Button>
            <Tooltip title="重新載入">
              <IconButton onClick={loadAnnouncements} disabled={isLoading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* 訊息提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* 統計卡片 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  公告總數
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats?.total || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  啟用中
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats?.active || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  已停用
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="text.secondary">
                  {stats?.inactive || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 公告列表 */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>類型</TableCell>
                <TableCell>標題</TableCell>
                <TableCell>優先序</TableCell>
                <TableCell>開始日期</TableCell>
                <TableCell>結束日期</TableCell>
                <TableCell>建立者</TableCell>
                <TableCell align="center">狀態</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {announcements.map((announcement) => (
                <TableRow
                  key={announcement.announcement_id}
                  sx={{ opacity: announcement.is_active ? 1 : 0.6 }}
                >
                  <TableCell>
                    <Chip
                      icon={typeIcons[announcement.type] as React.ReactElement}
                      label={typeNames[announcement.type]}
                      size="small"
                      color={typeColors[announcement.type]}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {announcement.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                      {announcement.content}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={announcement.priority} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(announcement.start_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(announcement.end_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {announcement.created_by_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={announcement.is_active === 1}
                      onChange={() => handleToggle(announcement)}
                      disabled={isProcessing}
                      color="success"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="編輯">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEdit(announcement)}
                        disabled={isProcessing}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="刪除">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDelete(announcement)}
                        disabled={isProcessing}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {announcements.length === 0 && !isLoading && (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">尚無公告</Typography>
          </Box>
        )}

        {/* 新增/編輯對話框 */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingAnnouncement ? '編輯公告' : '新增公告'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="標題"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />

              <TextField
                fullWidth
                label="內容"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                multiline
                rows={4}
                required
              />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>類型</InputLabel>
                    <Select
                      value={formData.type}
                      label="類型"
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                    >
                      <MenuItem value="info">一般資訊</MenuItem>
                      <MenuItem value="warning">警告通知</MenuItem>
                      <MenuItem value="success">成功訊息</MenuItem>
                      <MenuItem value="error">錯誤警示</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="優先序"
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                    helperText="數字越大越優先顯示"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="開始日期"
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="留空表示立即生效"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="結束日期"
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="留空表示永久有效"
                  />
                </Grid>
              </Grid>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_active === 1}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                  />
                }
                label="立即啟用"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>取消</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isProcessing}
            >
              {editingAnnouncement ? '更新' : '新增'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 刪除確認對話框 */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>確認刪除</DialogTitle>
          <DialogContent>
            <Typography>
              確定要刪除公告「{deletingAnnouncement?.title}」嗎？此操作無法復原。
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={isProcessing}
            >
              確認刪除
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default AdminAnnouncementsPage;
